import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDeal } from './useDeal';
import Image from '../../assets/Image';
import ReferAndEarn from './ReferAndEarn';
import { useNavigate } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import ShareIcon from '@mui/icons-material/Share';
import { getRupee, numDifferentiation, twoDecimalNumber } from '../../utils/numbers';
import Skeleton from '@mui/material/Skeleton';
import MultiInvoice from '../../assets/multi-invoice.png';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { ReactComponent as MultiInvoiceCards } from '../../assets/svg/multi.svg';
import { ReactComponent as CoinsPartner } from '../../assets/svg/CoinsPartner.svg';
import { FinanceType } from './types';
import clsx from 'clsx';
import TapReserve from '../DealDetail/TapReserve';
import { Box, Typography } from '@mui/material';
import { baseColors } from '../../assets/theme';
import { RemoteConfigContext } from '../../context/RemoteConfigContext';
import moment from 'moment';
import LoginIcon from '@mui/icons-material/Login';
import ChequeModal from '../../components/organisms/chequeModal/ChequeModal';
import { BONDS_EVENTS } from '../BondsDealDetail/constants';
import { useAnalytics } from '../../hooks/useAnalytics';
import DigitalGold from '../DealDetail/DigitalGold/DigitalGold';
import WhatsappQRComponent from '../../components/organisms/WhatsappQr/WhatsappQRComponent';
import { DEAL_EVENTS } from './constants';
import { useProfile } from '../../context/ProfileContext';

import RMCard from './RMCard';
import { ViewOnlyUntilSigned } from '../../components/organisms/AgreementGate/useAgreementGate';
import toast from 'react-hot-toast';
import useGetPartnerProfile from '../../hooks/useGetPartnerProfile';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import PreIpo from '../DealDetail/preIpo/PreIpo';
import DealsSortDropdown from '../../components/DealsSortDropdown';
import DealExplanationCarousel from './DealExplanationCarousel';
import OtherProducts from '../DealDetail/otherProducts/OtherPorducts';
import DigitalSilver from '../DealDetail/DigitalSilver/DigitalSilver';
import ListedBondsPage from '../DealDetail/ListedBonds';

export function findLiveEvent(events) {
  const now = new Date();

  for (const event of events) {
    const startTime = new Date(event.startTime);
    const endTime = new Date(event.endTime);

    if (now >= startTime && now <= endTime) {
      return {
        isLive: true,
        details: event,
      };
    }

    if (now < startTime) {
      return {
        isLive: false,
        details: event,
      };
    }
  }

  return null;
}

const calculateTab = (queryParams, profileData) => {
  if (!profileData) return;
  const tabParam = queryParams.get('tab');

  // Default to first available product type
  if (!tabParam) {
    if (profileData?.productTypes?.enableInvoiceDiscounting) return 'INVOICE_DISCOUNTING';
    if (profileData?.productTypes?.enableBond) return 'BOND';
    if (profileData?.productTypes?.enableGold) return 'GOLD';
    if (profileData?.productTypes?.enableSilver) return 'SILVER';
    if (profileData?.productTypes?.enableTapReserve) return 'TAP_RESERVE';
    return 'INVOICE_DISCOUNTING'; // fallback
  }

  return tabParam;
};

const Deals = () => {
  const { profileData } = useProfile();
  const [fullscreenBanner, setFullscreenBanner] = useState(null);
  const queryParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const [tab, setTab] = useState(calculateTab(queryParams, profileData));
  const [selectedDealType, setSelectedDealType] = useState('deluxe');
  const [sortBy, setSortBy] = useState('none');
  const [open, setOpen] = useState(false);
  const [liveCardDetails, setLiveCardDetails] = useState(null);
  const [liveCardDetailsLoading, setLiveCardDetailsLoading] = useState(true);
  const [openChequeModal, setOpenChequeModal] = useState(false);
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const { data: partnerData } = useGetPartnerProfile();

  useEffect(() => {
    setTab(calculateTab(queryParams, profileData));
  }, [profileData, queryParams]);

  const { data, isLoading } = useDeal();
  const { data: deals } = data || {};

  const { remoteConfig } = useContext(RemoteConfigContext);

  const flatPercentageDealPartner = remoteConfig?.flatPercentageDealPartner;
  const hideP2P = remoteConfig?.hideP2P;
  const partnerAdvertisement = remoteConfig?.partnerAdvertisement;
  const showPartnerArrearsCard = remoteConfig?.showPartnerArrearsCard;

  const handleDealClick = useCallback((id: string, financeType: FinanceType) => {
    navigate(`/deals/${id}/${financeType}`);
  }, []);

  const handleBondsClick = useCallback((id: string, type: string) => {
    navigate(`/bond-deals${type === 'PTC' ? '/PTC' : ''}/${id}`);
  }, []);

  const handleTabChange = useCallback(
    (event: React.SyntheticEvent, newValue: string) => {
      setTab(newValue);
      const queryParams = new URLSearchParams(window.location.search);
      queryParams.set('tab', newValue);

      if (newValue === 'INVOICE_DISCOUNTING') {
        track({ eventName: DEAL_EVENTS.Invoice_Discounting });
      } else if (newValue === 'BOND') {
        track({ eventName: DEAL_EVENTS.Bonds });
      } else if (newValue === 'GOLD') {
        track({ eventName: DEAL_EVENTS.Gold });
      } else if (newValue === 'SILVER') {
        track({ eventName: DEAL_EVENTS.Silver });
      }
      navigate(`?${queryParams.toString()}`);
    },
    [navigate],
  );

  const isInitialRender = useRef(true);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    if (!queryParams.get('tab')) {
      queryParams.set('tab', tab);
      navigate(`?${queryParams.toString()}`, { replace: true });
    }
  }, [tab, navigate]);

  const isEventTracked = useRef(false);

  useEffect(() => {
    if (isInitialRender.current && !isEventTracked.current) {
      track({ eventName: DEAL_EVENTS.Invoice_Discounting });
      isInitialRender.current = false;
      isEventTracked.current = true;
    }
  }, []);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleChequeModalOpen = () => setOpenChequeModal(true);
  const handleChequeModalClose = () => setOpenChequeModal(false);

  // Sort options for deals
  const sortOptions = [
    { label: 'XIRR: High to low', value: 'xirr-high' },
    { label: 'XIRR: Low to high', value: 'xirr-low' },
    { label: 'Tenure: High to low', value: 'tenure-high' },
    { label: 'Tenure: Low to high', value: 'tenure-low' },
  ];

  // Get available deal types for TABS (only show predefined types if they exist in data)
  const availableDealTypes = useMemo(() => {
    if (!deals) return [];

    // Define predefined deal types that are allowed in tabs
    const predefinedDealTypes = ['DELUXE DEAL', 'POWER HOUR', 'LEGENDS', 'SPOT LIGHT'];

    // Get all deal types from data
    const allDealTypes = new Set();
    deals.forEach((deal) => {
      const dealSubTypeName = deal?.meta?.dealConfiguration?.dealSubTypeName;

      if (dealSubTypeName) {
        allDealTypes.add(dealSubTypeName);
      }
    });

    // Only include predefined types that actually exist in the data
    const availableTypes = predefinedDealTypes.filter((type) => allDealTypes.has(type));

    return availableTypes;
  }, [deals]);

  // Auto-select the single deal type if only one is available
  useEffect(() => {
    if (availableDealTypes?.length === 1) {
      const singleDealType = availableDealTypes[0];
      const dealTypeMap = {
        deluxe: 'DELUXE DEAL',
        power: 'POWER HOUR',
        legends: 'LEGENDS',
        spotlight: 'SPOT LIGHT',
        earnGoldback: 'EARN GOLDBACK',
      };

      // Find the tab key for the single deal type
      const tabKey =
        Object.keys(dealTypeMap).find(
          (key) => dealTypeMap[key as keyof typeof dealTypeMap] === singleDealType,
        ) || 'deluxe';

      setSelectedDealType(tabKey);
    }
  }, [availableDealTypes]);

  const multiFirstDeals = useMemo(() => {
    if (!deals) return [];

    let result = deals;

    const multiDeals = result.filter((deal) => deal.financeType === 'POOLING');
    const normalDeals = result.filter(
      (deal) =>
        deal.financeType !== 'POOLING' &&
        deal.financeType !== 'SECONDARY_NON_CONVERTIBLE_DEBENTURES',
    );

    let sortedDeals = [...multiDeals, ...normalDeals];

    // Define deal type mapping
    const dealTypeMap = {
      deluxe: 'DELUXE DEAL',
      power: 'POWER HOUR',
      legends: 'LEGENDS',
      spotlight: 'SPOT LIGHT',
      earnGoldback: 'EARN GOLDBACK',
    };

    // Apply sorting based on sortBy selection
    if (sortBy && sortBy !== 'none') {
      sortedDeals = sortedDeals.sort((a, b) => {
        // First priority: Non-sold out deals come first (regardless of deal type)
        if (a.isSoldOut !== b.isSoldOut) {
          return a.isSoldOut ? 1 : -1;
        }

        // Second priority: Deal type filtering (if multiple deal types available)
        if (availableDealTypes?.length > 1) {
          const selectedDealTypeName = dealTypeMap[selectedDealType];
          const aIsSelectedType =
            a?.meta?.dealConfiguration?.dealSubTypeName === selectedDealTypeName;
          const bIsSelectedType =
            b?.meta?.dealConfiguration?.dealSubTypeName === selectedDealTypeName;

          if (aIsSelectedType && !bIsSelectedType) return -1;
          if (!aIsSelectedType && bIsSelectedType) return 1;
        }

        // Third priority: Sort by selected criteria
        switch (sortBy) {
          case 'xirr-high':
            return (b.minInvestment?.netIRR || 0) - (a.minInvestment?.netIRR || 0);
          case 'xirr-low':
            return (a.minInvestment?.netIRR || 0) - (b.minInvestment?.netIRR || 0);
          case 'tenure-high':
            return (b.tenure || 0) - (a.tenure || 0);
          case 'tenure-low':
            return (a.tenure || 0) - (b.tenure || 0);
          default:
            return 0;
        }
      });
    } else {
      // When no sort is selected, always prioritize sold out deals to the end
      sortedDeals = sortedDeals.sort((a, b) => {
        // First priority: Non-sold out deals come first (regardless of deal type)
        if (a.isSoldOut !== b.isSoldOut) {
          return a.isSoldOut ? 1 : -1;
        }

        // Second priority: Deal type filtering (if multiple deal types available)
        if (availableDealTypes?.length > 1) {
          const selectedDealTypeName = dealTypeMap[selectedDealType];
          const aIsSelectedType =
            a?.meta?.dealConfiguration?.dealSubTypeName === selectedDealTypeName;
          const bIsSelectedType =
            b?.meta?.dealConfiguration?.dealSubTypeName === selectedDealTypeName;

          if (aIsSelectedType && !bIsSelectedType) return -1;
          if (!aIsSelectedType && bIsSelectedType) return 1;
        }

        return 0;
      });
    }

    return sortedDeals;
  }, [deals, profileData?.type, selectedDealType, sortBy, availableDealTypes?.length]);

  const getBondsDeals = useMemo(() => {
    const name = profileData?.name;

    if (Array.isArray(deals)) {
      if (name === 'FUNDS_INDIA_SUB_PARTNER_CONFIG') {
        const filterPtcDeals = deals?.filter((deal) => deal?.isPtcDeal === true);
        return [...filterPtcDeals];
      }

      const bondsDeals = deals?.filter(
        (deal) => deal.financeType === 'SECONDARY_NON_CONVERTIBLE_DEBENTURES',
      );
      return [...bondsDeals];
    }

    return [];
  }, [deals, profileData?.name]);

  useEffect(() => {
    const partnerZoomWebinarDetails = remoteConfig.partnerZoomWebinarDetails;
    if (partnerZoomWebinarDetails && Object.keys(partnerZoomWebinarDetails).length > 0) {
      const liveEvent = findLiveEvent(partnerZoomWebinarDetails);
      if (liveEvent) setLiveCardDetails(liveEvent);
      setLiveCardDetailsLoading(false);
    } else {
      setLiveCardDetailsLoading(false);
    }
  }, [remoteConfig.partnerZoomWebinarDetails]);

  const NCDDealCard = () => {
    return (
      <div className="w-full border border-gray-200 rounded-lg shadow-none mb-6">
        <a
          href="https://tapinvestbonds.notion.site/Listed-NCD-Deals-Senior-Secured-Rated-c64e8655ab374a96af4e5a3a15b542e0"
          target="_blank"
          rel="noreferrer"
          className="bg-white rounded-t-lg p-4 items-left flex flex-col gap-3"
          style={{
            background:
              'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.04) 132.14%), #FFF',
            boxShadow: '0px 1.1px 1.8px -0.3px rgba(0, 0, 0, 0.10)',
          }}
        >
          <LoginIcon />

          <span className="ml-2 text-base font-semibold text-slate-800">
            Listed NCD Deals - Senior, Secured & Rated
          </span>
        </a>
        <div className="px-6 py-5 bg-white rounded-b-lg">
          <p className="text-sm text-gray-600">
            Reach out directly to{' '}
            <a href="mailto:avnish.singh@tapinvest.in" className="font-bold text-green-700">
              Avnish Singh (Bonds Manager)
            </a>
          </p>
          <p className="text-sm text-gray-600">
            <a href="mailto:avnish.singh@tapinvest.in" className="font-bold text-green-700">
              avnish.singh@tapinvest.in
            </a>{' '}
            or{' '}
            <a href="tel:+917278005041" className="font-bold text-green-700">
              +91 7278005041
            </a>
          </p>
        </div>
      </div>
    );
  };

  const ArrearsCard = () => {
    return (
      <div
        className={`min-h-40 p-5 border bg-white border-zinc-200 rounded-lg relative overflow-hidden`}
      >
        {/* Background coins image */}
        <div className="absolute right-0 bottom-0 pointer-events-none">
          <CoinsPartner className="w-32 h-full" />
        </div>

        <h3 className="text-lg font-semibold mb-2">Next Day Commission</h3>
        <p className="text-stone-500 text-sm font-normal mb-7">
          You can now receive next day commissions on investment for the current week
        </p>
        {isLoading ? (
          <Skeleton width="100%" height={40} />
        ) : (
          <div className="flex flex-col">
            <p className="text-neutral-400 text-xs">Commission received so far</p>
            <p className="text-neutral-800 font-semibold text-[26px]">
              ₹{partnerData?.data?.partnerAggregate?.arrears || 0}
            </p>
          </div>
        )}
      </div>
    );
  };

  // Filter banners based on partner's publicId
  const filteredMarketingBanners = useMemo(() => {
    if (!remoteConfig?.marketingBanner?.length) return [];

    const currentPublicId = profileData?.publicId;

    return remoteConfig.marketingBanner.filter((banner) => {
      // First check hideFrom - if partner is in hideFrom list, don't show the banner
      if (banner.hideFrom && banner.hideFrom.length > 0) {
        if (banner.hideFrom.includes(currentPublicId)) {
          return false; // Explicitly hide from this partner
        }
      }

      // Then check targetPublicIds - if specified, only show to those partners
      if (banner.targetPublicIds && banner.targetPublicIds.length > 0) {
        return banner.targetPublicIds.includes(currentPublicId);
      }

      // If neither hideFrom nor targetPublicIds is specified, show to everyone (backward compatible)
      return true;
    });
  }, [remoteConfig?.marketingBanner, profileData?.publicId]);

  const MarketingBannerCarousel = ({ banners }) => {
    const settings = {
      infinite: banners?.length > 1,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: banners?.length > 1,
      // nextArrow: banners.length > 1 ? <CustomNextArrow /> : null,
      // prevArrow: banners.length > 1 ? <CustomPrevArrow /> : null,
      dots: banners?.length > 1,
      dotsClass: 'slick-dots custom-dots',
      autoplay: banners?.length > 1,
      autoplaySpeed: 5000,
      appendDots: (dots) => (
        <div>
          <ul style={{ margin: '0', padding: '0' }}>{dots}</ul>
        </div>
      ),
    };

    const handleBannerClick = (banner) => {
      // Show fullscreen modal if fullSizeImage is available
      if (banner.fullSizeImage) {
        setFullscreenBanner(banner);
      } else {
        // Fallback to original behavior
        banner.redirectUrl && window.open(banner.redirectUrl, '_blank');
        banner?.triggerEvent &&
          track({
            eventName: banner.triggerEvent,
            eventProperties: {
              redirectUrl: banner.redirectUrl || '',
              imageUrl: banner.imageUrl || '',
              partnerId: profileData?.id || '',
              partnerPublicId: partnerData?.data?.profile?.publicId || '',
              partnerName:
                partnerData?.data?.profile?.firstName +
                  ' ' +
                  partnerData?.data?.profile?.lastName || '',
              partnerType: profileData?.type || '',
              partnerEmail: profileData?.email || '',
              partnerPhone: partnerData?.data?.profile?.phone || '',
            },
          });
        banner.successMessage &&
          toast.success(banner.successMessage, {
            duration: 5000,
          });
      }
    };

    return (
      <div className="marketing-banner-carousel w-full">
        <Slider {...settings}>
          {banners?.map((banner, index) => (
            <div
              key={index}
              className={`outline-none ${banner.showCursorPointer ? 'cursor-pointer' : ''}`}
            >
              <Image
                // @ts-ignore
                src={banner.imageUrl}
                alt={banner.altText || 'Marketing Banner'}
                className={`rounded-xl w-full `}
                onClick={() => handleBannerClick(banner)}
              />
            </div>
          ))}
        </Slider>
      </div>
    );
  };

  // Fullscreen Banner Modal Component
  const FullscreenBannerModal = ({ banner, onClose }) => {
    if (!banner) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-90 z-[100] flex justify-center items-center">
        <div className="relative max-w-7xl w-full h-full flex items-center justify-center p-4">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-full p-2 transition-all duration-200"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          {/* Fullscreen Image */}
          <div className="w-full h-full flex items-center justify-center">
            <Image
              // @ts-ignore
              src={banner.fullSizeImage || banner.imageUrl}
              alt={banner.altText || 'Marketing Banner'}
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => {
                e.stopPropagation();
                // Handle banner click actions if needed
                if (banner.redirectUrl) {
                  window.open(banner.redirectUrl, '_blank');
                }
                if (banner.triggerEvent) {
                  track({
                    eventName: banner.triggerEvent,
                    eventProperties: {
                      redirectUrl: banner.redirectUrl || '',
                      imageUrl: banner.imageUrl || '',
                      partnerId: partnerData?.data?.profile?.id || '',
                      partnerPublicId: partnerData?.data?.profile?.publicId || '',
                      partnerName:
                        partnerData?.data?.profile?.firstName +
                          ' ' +
                          partnerData?.data?.profile?.lastName || '',
                      partnerType: profileData?.type || '',
                      partnerEmail: partnerData?.data?.profile?.email || '',
                      partnerPhone: partnerData?.data?.profile?.phone || '',
                    },
                  });
                }
                if (banner.successMessage) {
                  toast.success(banner.successMessage, {
                    duration: 3000,
                  });
                }
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="relative">
      <ViewOnlyUntilSigned>
      <div className="mt-4 flex items-center max-w-7xl mx-auto w-full">
        {profileData?.clientActions?.enableMarketingBanner &&
        filteredMarketingBanners?.length > 0 ? (
          filteredMarketingBanners.length === 1 ? (
            <Image
              // @ts-ignore
              src={filteredMarketingBanners[0].imageUrl}
              alt={filteredMarketingBanners[0].altText || 'Marketing Banner'}
              className={`rounded-xl w-full ${
                filteredMarketingBanners[0].redirectUrl || filteredMarketingBanners[0].triggerEvent
                  ? 'cursor-pointer'
                  : ''
              }`}
              onClick={() => {
                // Show fullscreen modal if fullSizeImage is available
                if (filteredMarketingBanners[0].fullSizeImage) {
                  setFullscreenBanner(filteredMarketingBanners[0]);
                } else {
                  // Fallback to original behavior
                  filteredMarketingBanners[0].redirectUrl &&
                    window.open(filteredMarketingBanners[0].redirectUrl, '_blank');
                  filteredMarketingBanners[0]?.triggerEvent &&
                    track({
                      eventName: filteredMarketingBanners[0].triggerEvent,
                      eventProperties: {
                        redirectUrl: filteredMarketingBanners[0].redirectUrl || '',
                        imageUrl: filteredMarketingBanners[0].imageUrl || '',
                        partnerId: partnerData?.data?.profile?.id || '',
                        partnerPublicId: partnerData?.data?.profile?.publicId || '',
                        partnerName:
                          partnerData?.data?.profile?.firstName +
                            ' ' +
                            partnerData?.data?.profile?.lastName || '',
                        partnerType: profileData?.type || '',
                        partnerEmail: partnerData?.data?.profile?.email || '',
                        partnerPhone: partnerData?.data?.profile?.phone || '',
                      },
                    });
                  filteredMarketingBanners[0].successMessage &&
                    toast.success(filteredMarketingBanners[0].successMessage, {
                      duration: 3000,
                    });
                }
              }}
            />
          ) : (
            <MarketingBannerCarousel banners={filteredMarketingBanners} />
          )
        ) : null}
      </div>
      </ViewOnlyUntilSigned>

      {/* Cheque Banner */}
      {/* {profileData?.clientActions?.enableSupportCard && (
        <Box className="py-3 px-5 flex gap-3 !max-w-7xl border bg-stone-100 border-stone-200 mt-6 mx-auto rounded-xl">
          <NoteOutlinedIcon
            style={{ height: '28px', width: '26px' }}
            className="bg-stone-200 rounded-full p-1"
          />
          <Typography className="font-medium text-sm">
            We introduced a new way to add money to your Client's virtual account - via cheque.{' '}
            <Box
              component="span"
              className="underline underline-offset-2 font-semibold text-slate-500  decoration-slate-500 cursor-pointer"
              onClick={handleChequeModalOpen}
            >
              Click here to know more
            </Box>
          </Typography>
        </Box>
      )} */}
      <Tabs
        className="mt-8 max-w-7xl mx-auto"
        classes={{
          indicator: '!bg-green-700',
          flexContainer: 'border-b border-b-black/[0.08]',
        }}
        value={tab}
        onChange={handleTabChange}
        aria-label="deals-tabs"
      >
        {profileData?.productTypes?.enableInvoiceDiscounting && (
          <Tab
            value="INVOICE_DISCOUNTING"
            label="Invoice Discounting"
            className="!capitalize !font-medium"
            classes={{
              selected: '!text-green-700',
            }}
            id="invoice-discounting"
            aria-controls="deals-tabpanel-INVOICE_DISCOUNTING"
          />
        )}
        {profileData?.productTypes?.enableListedBond && (
          <Tab
            value="LISTED_BONDS"
            label="Listed Bonds"
            id="listed-bonds"
            className="!capitalize !font-medium"
            classes={{
              selected: '!text-green-700',
            }}
            aria-controls="deals-tabpanel-LISTED_BONDS"
          />
        )}
        {profileData?.productTypes?.enableBond && (
          <Tab
            value="BOND"
            label="Secured Invoice Discounting"
            id="bonds"
            className="!capitalize !font-medium"
            classes={{
              selected: '!text-green-700',
            }}
            aria-controls="deals-tabpanel-BOND"
          />
        )}
        {profileData?.productTypes?.enableGold && (
          <Tab
            value="GOLD"
            label="Digital Gold"
            className="!capitalize !font-medium"
            classes={{
              selected: '!text-green-700',
            }}
            id="digital-gold"
            aria-controls="deals-tabpanel-GOLD"
          />
        )}

        {profileData?.productTypes?.enableSilver && (
          <Tab
            value="SILVER"
            label="Digital Silver"
            className="!capitalize !font-medium"
            classes={{
              selected: '!text-green-700',
            }}
            id="digital-silver"
            aria-controls="deals-tabpanel-SILVER"
          />
        )}

        {profileData?.productTypes?.enableTapReserve && !hideP2P && (
          <Tab
            value="TAP_RESERVE"
            label="Tap Reserve"
            id="tapReserve"
            className="!capitalize !font-medium"
            classes={{
              selected: '!text-green-700',
            }}
            aria-controls="deals-tabpanel-TAP_RESERVE"
          />
        )}
        {profileData?.productTypes?.enablePreIPO && (
          <Tab
            value="PRE_IPO"
            label="Pre-IPO"
            id="pre-ipo"
            className="!capitalize !font-medium"
            classes={{
              selected: '!text-green-700',
            }}
            aria-controls="deals-tabpanel-PRE_IPO"
          />
        )}

        {profileData?.productTypes?.enableOtherProducts && (
          <Tab
            value="OTHER_PRODUCTS"
            label="Other Products"
            className="!capitalize !font-medium"
            classes={{
              selected: '!text-green-700',
            }}
            id="other-products"
            aria-controls="deals-tabpanel-OTHER_PRODUCTS"
          />
        )}
      </Tabs>
      {/* Deals are view-only until the partner agreement is e-signed; the tab bar stays usable. */}
      <ViewOnlyUntilSigned>
      {/* Invoice Discounting Tab Panel */}
      {profileData?.productTypes?.enableInvoiceDiscounting && (
        <div
          role="tabpanel"
          hidden={tab !== 'INVOICE_DISCOUNTING'}
          id={`deals-tabpanel-INVOICE_DISCOUNTING`}
          aria-labelledby="invoice-discounting"
          className={tab !== 'INVOICE_DISCOUNTING' && 'hidden'}
        >
          <div className="grid xl:grid-cols-3 gap-8 my-6 max-w-7xl mx-auto">
            <div className="flex xl:col-span-2 flex-col gap-6">
              <button
                type="button"
                className="text-sm xl:hidden flex gap-2 text-slate-600 px-4 py-2.5 w-fit h-10 border border-gray-400 rounded-lg bg-white"
                onClick={handleOpen}
              >
                <ShareIcon fontSize="small" />
                Invite Client
              </button>
              <div
                className={`flex items-center ${
                  availableDealTypes.length > 1 ? 'justify-between' : 'justify-end'
                }`}
              >
                {/* {availableDealTypes.length > 1 && (
                  <div className="bg-stone-100 border border-stone-200 rounded-lg p-1 pl-2 w-fit relative">
                    <div
                      className="absolute top-1 bottom-1 bg-white rounded-md shadow-sm transition-all duration-300 ease-in-out"
                      style={{
                        width: '85px',
                        left: (() => {
                          // Find the actual index of the selected deal type in availableDealTypes
                          const dealTypeMap = {
                            deluxe: 'DELUXE DEAL',
                            power: 'POWER HOUR',
                            legends: 'LEGENDS',
                            spotlight: 'SPOT LIGHT',
                          };

                          const selectedDealTypeName =
                            dealTypeMap[selectedDealType as keyof typeof dealTypeMap];
                          const actualIndex = availableDealTypes.findIndex(
                            (dealType) => dealType === selectedDealTypeName,
                          );

                          return actualIndex >= 0 ? `${8 + actualIndex * 89}px` : '8px';
                        })(),
                      }}
                    />
                    <div className="flex gap-1 relative z-10">
                      {availableDealTypes.map((dealType: string, index: number) => {
                        // Define deal type mapping for display
                        const dealTypeMap = {
                          deluxe: 'DELUXE DEAL',
                          power: 'POWER HOUR',
                          legends: 'LEGENDS',
                          spotlight: 'SPOT LIGHT',
                        };

                        // Map deal type name to tab key
                        const tabKey = (Object.keys(dealTypeMap).find(
                          (key) => dealTypeMap[key as keyof typeof dealTypeMap] === dealType,
                        ) || 'deluxe') as string;

                        // Map deal type name to display name
                        const displayName: string =
                          dealType === 'DELUXE DEAL'
                            ? 'Deluxe Deal'
                            : dealType === 'POWER HOUR'
                            ? 'Power Hour'
                            : dealType === 'LEGENDS'
                            ? 'Legends'
                            : dealType === 'SPOT LIGHT'
                            ? 'Spotlight'
                            : dealType;

                        return (
                          <button
                            key={dealType}
                            type="button"
                            className={`px-[5px] py-[10px] rounded-md text-xs font-semibold transition-colors whitespace-nowrap relative w-[85px] text-center ${
                              selectedDealType === tabKey
                                ? 'text-stone-900'
                                : 'text-stone-500 hover:text-stone-800'
                            }`}
                            onClick={() => setSelectedDealType(tabKey)}
                          >
                            {displayName}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )} */}
                <div></div>
                {/* Sort Dropdown */}
                <DealsSortDropdown
                  options={sortOptions}
                  value={sortBy}
                  onChange={setSortBy}
                  className="w-fit"
                />
              </div>

              {isLoading ? (
                <>
                  <div className="relative cursor-pointer bg-white rounded-lg overflow-hidden border border-zinc-200">
                    <div className="px-4 sm:px-6 py-4 flex items-start md:items-center gap-3">
                      <Skeleton width={40} height={40} />
                      <Skeleton width="100%" height={40} />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 px-4 sm:px-6 py-4 bg-neutral-50 border-t border-t-zinc-200">
                      <Skeleton width="100%" height={60} />
                      <Skeleton width="100%" height={60} />
                      <Skeleton width="100%" height={60} />
                      <Skeleton width="100%" height={60} />
                    </div>
                  </div>
                  <div className="relative cursor-pointer bg-white rounded-lg overflow-hidden border border-zinc-200">
                    <div className="px-4 sm:px-6 py-4 flex items-start md:items-center gap-3">
                      <Skeleton width={40} height={40} />
                      <Skeleton width="100%" height={40} />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 px-4 sm:px-6 py-4 bg-neutral-50 border-t border-t-zinc-200">
                      <Skeleton width="100%" height={60} />
                      <Skeleton width="100%" height={60} />
                      <Skeleton width="100%" height={60} />
                      <Skeleton width="100%" height={60} />
                    </div>
                  </div>{' '}
                  <div className="relative cursor-pointer bg-white rounded-lg overflow-hidden border border-zinc-200">
                    <div className="px-4 sm:px-6 py-4 flex items-start md:items-center gap-3">
                      <Skeleton width={40} height={40} />
                      <Skeleton width="100%" height={40} />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 px-4 sm:px-6 py-4 bg-neutral-50 border-t border-t-zinc-200">
                      <Skeleton width="100%" height={60} />
                      <Skeleton width="100%" height={60} />
                      <Skeleton width="100%" height={60} />
                      <Skeleton width="100%" height={60} />
                    </div>
                  </div>
                </>
              ) : !isLoading && multiFirstDeals.length > 0 ? (
                multiFirstDeals.map((deal) => {
                  const isPooling = deal.financeType === 'POOLING';

                  // Get deal type for styling
                  const dealSubTypeName = deal?.meta?.dealConfiguration?.dealSubTypeName;

                  // Define color mapping for deal types
                  const getDealTypeColors = (dealType) => {
                    switch (dealType) {
                      case 'DELUXE DEAL':
                        return {
                          border: 'border-amber-600',
                          badge: 'bg-amber-600',
                        };
                      case 'POWER HOUR':
                        return {
                          border: 'border-emerald-600',
                          badge: 'bg-emerald-600',
                        };
                      case 'LEGENDS':
                        return {
                          border: 'border-emerald-600',
                          badge: 'bg-emerald-600',
                        };
                      case 'NEW LISTING':
                        return {
                          border: 'border-indigo-600',
                          badge: 'bg-indigo-600',
                        };
                      default:
                        if (dealType)
                          return {
                            border: 'border-sky-600',
                            badge: 'bg-sky-600',
                          };
                        return { badge: 'bg-sky-600' };
                    }
                  };

                  const colors = getDealTypeColors(dealSubTypeName);

                  return (
                    <div key={deal.id} className="relative">
                      <div
                        onClick={() =>
                          !deal?.isSoldOut && handleDealClick(deal.id, deal.financeType)
                        }
                        className={clsx(
                          'relative z-[1] bg-white rounded-lg overflow-hidden border-2',
                          colors.border,
                          {
                            'cursor-pointer': !deal?.isSoldOut,
                          },
                        )}
                      >
                        {isPooling && (
                          <div className="uppercase flex items-center gap-2 absolute right-0 top-0 w-24 rounded-tr rounded-bl bg-lime-500 text-white py-2 px-3 text-xs font-semibold">
                            <MultiInvoiceCards />
                            Multi
                          </div>
                        )}
                        <div className="flex justify-between">
                          <div className="px-4 sm:px-6 py-4 flex items-start md:items-center gap-3">
                            <div className="min-w-9 max-w-9 min-h-9 max-h-9 md:max-h-12 md:min-w-12 md:min-h-12 overflow-hidden rounded flex items-center justify-center flex-shrink-0">
                              <Image
                                // @ts-ignore
                                src={isPooling ? MultiInvoice : deal?.company?.logoUrl}
                                alt={deal?.name}
                                className="object-contain"
                              />
                            </div>
                            <div>
                              <h3 className="text-lg mb-1">{deal?.company?.brandName || ''}</h3>
                              <p className="line-clamp-2 md:truncate text-slate-400 text-sm max-w-xl">
                                {deal?.miniSummary}
                              </p>
                            </div>
                          </div>

                          {(deal?.meta?.dealConfiguration?.dealSubType === 'ALL_CLIENTS' ||
                            deal?.meta?.dealConfiguration?.dealSubType === 'PARTNER_ONLY' ||
                            deal?.meta?.dealConfiguration?.dealSubType ===
                              'ALL_CLIENTS_PARTNER_TAGS') &&
                            deal?.meta?.dealConfiguration?.dealSubTypeName && (
                              <div
                                className={`relative overflow-hidden px-3 py-2 h-fit text-xs font-semibold text-white flex-shrink-0 ${colors.badge}`}
                                style={{ borderRadius: '0px 4px 0px 4px' }}
                              >
                                <span className="z-10 relative">
                                  {deal?.meta?.dealConfiguration?.dealSubTypeName?.length > 20
                                    ? `${deal?.meta?.dealConfiguration?.dealSubTypeName?.slice(
                                        0,
                                        20,
                                      )}...`
                                    : deal?.meta?.dealConfiguration?.dealSubTypeName}
                                </span>
                                <div className="shine-strip"></div>
                              </div>
                            )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 max-md:gap-2 px-4 sm:px-6 py-4 bg-neutral-50 border-t border-t-zinc-200">
                          <div>
                            <h3 className="uppercase text-xs text-black/40 font-semibold">
                              Min Invt.
                            </h3>
                            <h4 className="font-medium text-gray-700">
                              {getRupee.format(deal.minInvestment.amount)}
                            </h4>
                          </div>
                          {/* TODO: Replace with Maturity Date */}
                          <div>
                            <h3 className="uppercase text-xs text-black/40 font-semibold">
                              Tenure
                            </h3>
                            <h4 className="font-medium text-gray-700">
                              {deal.tenure} {deal.tenurePeriod}
                            </h4>
                          </div>
                          {/*  */}
                          <div>
                            <h3 className="uppercase text-xs text-black/40 font-semibold">
                              Net Yield
                            </h3>
                            <h4 className="font-medium text-green-700">
                              {twoDecimalNumber(deal.minInvestment.netIRR)}%
                            </h4>
                          </div>
                          {/*  */}
                          <div>
                            <h3 className="uppercase text-xs text-black/40 font-semibold">
                              Raised
                            </h3>
                            <h4 className="font-medium text-gray-700">
                              {numDifferentiation(deal.successfullyAmountRaise)} /{' '}
                              {numDifferentiation(deal.targetAmountToRaise)}
                            </h4>
                          </div>
                        </div>
                        {flatPercentageDealPartner.some((item) => item == deal?.id) && (
                          <div className="bg-green-700 text-white p-4 md:p-2 text-sm font-semibold">
                            <p className="line-clamp-2 md:truncate text-white text-sm max-w-xl">
                              Exclusive deal with 2% to 2.5% commission.
                              <span className="text-xs">*Terms and conditions apply.</span>
                            </p>
                          </div>
                        )}
                      </div>
                      {deal?.isSoldOut && (
                        <div className="w-full relative bottom-2 uppercase text-amber-600 text-sm font-semibold text-center pb-2 pt-4 bg-amber-100 rounded-b-lg">
                          Sold out
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="py-4">
                  <Typography variant="h6" className="text-gray-600">
                    No deals are available at the moment.
                  </Typography>
                </div>
              )}
            </div>

            <div className="relative flex flex-col gap-2">
              {/* Arrears card */}
              {partnerData?.data?.partnerAggregate?.arrears > 0 && showPartnerArrearsCard && (
                <ArrearsCard />
              )}

              {/* NCD Deal Card */}
              {profileData?.clientActions?.enableNcdDealsCard && tab === 'BOND' && <NCDDealCard />}

              {/* Refer and Earn */}
              {profileData?.features?.enableReferral && <ReferAndEarn />}

              {/* deal Explanation section carousel */}
              <DealExplanationCarousel />

              {/* RMCard */}
              {profileData?.clientActions?.enableRMCard && <RMCard />}

              <div>
                {/* Live Card Loading State */}
                {liveCardDetailsLoading && profileData?.clientActions?.enableShowOnlineEvents && (
                  <Box
                    className="min-h-40 border bg-white border-zinc-200 rounded-lg"
                    sx={{
                      border: '1px solid ' + baseColors.zinc[200],
                      borderRadius: '8px',
                      display: 'grid',
                      alignItems: 'center',
                      cursor: 'pointer',
                      overflow: 'hidden',
                      gridTemplateColumns: '150px 1fr',
                    }}
                  >
                    <Skeleton
                      variant="rectangular"
                      width={150}
                      height="100%"
                      sx={{ bgcolor: baseColors.zinc[200] }}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'start',
                        justifyContent: 'space-between',
                        padding: '16px',
                        rowGap: '8px',
                      }}
                    >
                      <Skeleton
                        variant="rectangular"
                        width="60%"
                        height={20}
                        sx={{ borderRadius: '8px' }}
                      />
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'start',
                          flexDirection: 'column',
                          rowGap: '8px',
                        }}
                      >
                        <Skeleton
                          variant="text"
                          width="80%"
                          height={20}
                          sx={{ bgcolor: baseColors.zinc[300] }}
                        />
                        <Skeleton
                          variant="text"
                          width="60%"
                          height={15}
                          sx={{ bgcolor: baseColors.zinc[300] }}
                        />
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Live Card Loaded State */}
                {liveCardDetails && profileData?.clientActions?.enableShowOnlineEvents && (
                  <Box
                    className="min-h-40 border bg-white border-zinc-200 rounded-lg"
                    sx={{
                      border: '1px solid ' + baseColors.zinc[200],
                      borderRadius: '8px',
                      marginBottom: 2,
                      display: 'grid',
                      alignItems: 'center',
                      cursor: 'pointer',
                      overflow: 'hidden',
                      gridTemplateColumns: '150px 1fr',
                    }}
                    onClick={() => {
                      window.open(
                        liveCardDetails?.details?.youtubeLiveUrl ||
                          liveCardDetails?.details?.zoomUrl,
                        '_blank',
                      );
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        background: liveCardDetails?.details?.videoId
                          ? `url(https://img.youtube.com/vi/${liveCardDetails.details.videoId}/0.jpg) left center`
                          : `url(${liveCardDetails?.details?.thumbnailUrl}) left center`,
                        backgroundSize: liveCardDetails?.details?.videoId ? 'auto 140%' : 'cover',
                      }}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'start',
                        justifyContent: 'space-between',
                        padding: '16px',
                        rowGap: '8px',
                      }}
                    >
                      {/* Live Status */}
                      <Box
                        sx={{
                          backgroundColor: liveCardDetails?.isLive
                            ? baseColors.red[500]
                            : baseColors.stone[200],
                          color: liveCardDetails?.isLive ? baseColors.white : baseColors.stone[700],
                          padding: '4px 8px',
                          borderRadius: '8px',
                          fontSize: '10px',
                          fontWeight: '500',
                          textTransform: 'uppercase',
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: '8px',
                        }}
                      >
                        {/* Live Icon */}
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          style={{ marginRight: '6px' }}
                        >
                          <path
                            d="M3.17213 2.46879C3.21861 2.51523 3.25549 2.57037 3.28065 2.63107C3.30582 2.69177 3.31877 2.75684 3.31877 2.82254C3.31877 2.88825 3.30582 2.95331 3.28065 3.01401C3.25549 3.07471 3.21861 3.12986 3.17213 3.17629C2.42184 3.9267 2.00035 4.9444 2.00035 6.00554C2.00035 7.06669 2.42184 8.08439 3.17213 8.83479C3.21988 8.88092 3.25797 8.93609 3.28418 8.99709C3.31038 9.05809 3.32417 9.1237 3.32475 9.19009C3.32533 9.25648 3.31268 9.32232 3.28754 9.38377C3.2624 9.44522 3.22527 9.50104 3.17832 9.54799C3.13138 9.59494 3.07555 9.63206 3.0141 9.6572C2.95265 9.68234 2.88681 9.695 2.82042 9.69442C2.75403 9.69384 2.68842 9.68005 2.62742 9.65384C2.56642 9.62764 2.51125 9.58955 2.46513 9.54179C0.511625 7.58879 0.511625 4.42179 2.46513 2.46879C2.55889 2.37506 2.68604 2.3224 2.81863 2.3224C2.95121 2.3224 3.07836 2.37506 3.17213 2.46879ZM9.53813 2.46879C11.4911 4.42229 11.4911 7.58879 9.53813 9.54179C9.44437 9.63561 9.31719 9.68835 9.18455 9.6884C9.05192 9.68844 8.9247 9.6358 8.83087 9.54204C8.73705 9.44829 8.68432 9.32111 8.68427 9.18847C8.68423 9.05584 8.73687 8.92861 8.83063 8.83479C9.58091 8.08439 10.0024 7.06669 10.0024 6.00554C10.0024 4.9444 9.58091 3.9267 8.83063 3.17629C8.7368 3.08247 8.6841 2.95523 8.6841 2.82254C8.6841 2.68986 8.7368 2.56261 8.83063 2.46879C8.92445 2.37497 9.05169 2.32227 9.18437 2.32227C9.31706 2.32227 9.4443 2.37497 9.53813 2.46879ZM4.65563 3.90579C4.74936 3.99956 4.80202 4.12671 4.80202 4.25929C4.80202 4.39188 4.74936 4.51903 4.65563 4.61279C4.4736 4.7948 4.32921 5.01088 4.2307 5.24869C4.13219 5.4865 4.08148 5.74139 4.08148 5.99879C4.08148 6.2562 4.13219 6.51109 4.2307 6.7489C4.32921 6.98671 4.4736 7.20279 4.65563 7.38479C4.70205 7.43125 4.73887 7.48639 4.76398 7.54708C4.78909 7.60776 4.802 7.6728 4.80198 7.73847C4.80195 7.80414 4.78899 7.86917 4.76384 7.92984C4.73869 7.9905 4.70183 8.04562 4.65538 8.09204C4.60892 8.13847 4.55378 8.17528 4.49309 8.20039C4.43241 8.22551 4.36737 8.23842 4.3017 8.2384C4.23602 8.23837 4.171 8.22541 4.11033 8.20026C4.04967 8.17511 3.99455 8.13825 3.94813 8.09179C3.39304 7.53669 3.0812 6.78382 3.0812 5.99879C3.0812 5.21377 3.39304 4.4609 3.94813 3.90579C3.99456 3.85931 4.04971 3.82243 4.1104 3.79726C4.1711 3.7721 4.23617 3.75915 4.30188 3.75915C4.36758 3.75915 4.43265 3.7721 4.49335 3.79726C4.55404 3.82243 4.60919 3.85931 4.65563 3.90579ZM8.13462 3.90579C8.68971 4.4609 9.00155 5.21377 9.00155 5.99879C9.00155 6.78382 8.68971 7.53669 8.13462 8.09179C8.04032 8.18287 7.91402 8.23327 7.78292 8.23213C7.65183 8.23099 7.52642 8.17841 7.43372 8.0857C7.34101 7.993 7.28843 7.86759 7.28729 7.73649C7.28615 7.6054 7.33655 7.47909 7.42763 7.38479C7.60965 7.20279 7.75404 6.98671 7.85255 6.7489C7.95106 6.51109 8.00177 6.2562 8.00177 5.99879C8.00177 5.74139 7.95106 5.4865 7.85255 5.24869C7.75404 5.01088 7.60965 4.7948 7.42763 4.61279C7.33655 4.51849 7.28615 4.39219 7.28729 4.26109C7.28843 4.12999 7.34101 4.00459 7.43372 3.91188C7.52642 3.81918 7.65183 3.7666 7.78292 3.76546C7.91402 3.76432 8.04032 3.81471 8.13462 3.90579ZM6.04163 5.29079C6.24054 5.29079 6.4313 5.36981 6.57196 5.51046C6.71261 5.65112 6.79163 5.84188 6.79163 6.04079C6.79163 6.23971 6.71261 6.43047 6.57196 6.57112C6.4313 6.71178 6.24054 6.79079 6.04163 6.79079C5.84271 6.79079 5.65195 6.71178 5.51129 6.57112C5.37064 6.43047 5.29163 6.23971 5.29163 6.04079C5.29163 5.84188 5.37064 5.65112 5.51129 5.51046C5.65195 5.36981 5.84271 5.29079 6.04163 5.29079Z"
                            fill={
                              liveCardDetails?.isLive ? baseColors.white : baseColors.stone[500]
                            }
                          />
                        </svg>
                        {liveCardDetails?.isLive
                          ? 'Watch now'
                          : 'Going live on ' +
                            moment(liveCardDetails?.details?.startTime).format('DD MMM, h:mm A')}
                      </Box>

                      {/* Title and Platform */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'start',
                          flexDirection: 'column',
                          rowGap: '8px',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: baseColors.stone[900],
                          }}
                        >
                          {liveCardDetails?.details?.topic}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '12px',
                            fontWeight: '400',
                            color: baseColors.stone[500],
                          }}
                        >
                          {liveCardDetails?.details?.youtubeLiveUrl
                            ? 'YouTube Live'
                            : 'Zoom Webinar'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* WhatsApp Community Card */}
                {partnerAdvertisement?.[0]?.showWhatsappCommunityImage &&
                  profileData?.clientActions?.enableWhatsappCard && (
                    <Box
                      className="flex flex-col mt-2 cursor-pointer"
                      onClick={() => {
                        track({
                          eventName: DEAL_EVENTS.Join_Whatsapp_Community,
                        });
                        window.open(partnerAdvertisement?.[0]?.redirectionUrl);
                      }}
                    >
                      <Image
                        // @ts-ignore
                        src={partnerAdvertisement[0]?.ImageUrl}
                        alt="whatsapp community"
                        height={300}
                      />
                    </Box>
                  )}

                {/* Support Card */}
                {profileData?.clientActions?.enableSupportCard && (
                  <Box className="flex flex-col p-4 pl-0 mt-2">
                    <Typography className="!text-sm font-normal text-slate-800">
                      A Product By{' '}
                      <span className="font-medium" style={{ color: '#00000A' }}>
                        Tap Invest
                      </span>
                    </Typography>
                    <Typography className="!text-sm font-normal text-slate-800 mt-2">
                      Facing Issue? Contact us at:{' '}
                      <a
                        href="mailto:support@tapinvest.in"
                        className="cursor-pointer underline underline-offset-2 text-sm font-medium"
                        style={{ color: '#00000A' }}
                      >
                        support@tapinvest.in
                      </a>
                    </Typography>
                  </Box>
                )}
              </div>
            </div>

            {/* Refer and Earn Mobile Modal */}
            {profileData?.features?.enableReferral && (
              <Dialog maxWidth="sm" fullWidth onClose={handleClose} open={open}>
                <ReferAndEarn />
              </Dialog>
            )}
          </div>
        </div>
      )}
      {/* Bonds Tab Panel */}
      {profileData?.productTypes?.enableBond && (
        <div
          role="tabpanel"
          hidden={tab !== 'BOND'}
          id={`deals-tabpanel-BOND`}
          aria-labelledby="bonds"
          className={tab !== 'BOND' ? 'hidden' : 'undefined'}
        >
          <div className="grid xl:grid-cols-3 gap-8 my-6 max-w-7xl mx-auto">
            <div className="flex xl:col-span-2 flex-col gap-6">
              {isLoading && (
                <div className="relative cursor-pointer bg-white rounded-lg overflow-hidden border border-zinc-200">
                  <div className="px-4 sm:px-6 py-4 flex items-start md:items-center gap-3">
                    <Skeleton width={40} height={40} />
                    <Skeleton width="100%" height={40} />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 px-4 sm:px-6 py-4 bg-neutral-50 border-t border-t-zinc-200">
                    <Skeleton width="100%" height={60} />
                    <Skeleton width="100%" height={60} />
                    <Skeleton width="100%" height={60} />
                    <Skeleton width="100%" height={60} />
                  </div>
                </div>
              )}

              {!isLoading &&
                getBondsDeals?.map((deal) => {
                  return (
                    <div key={deal.id} className="relative">
                      <div
                        onClick={() => {
                          if (!deal?.isSoldOut) {
                            handleBondsClick(deal?.id, deal?.isPtcDeal ? 'PTC' : 'Bond');
                          }
                          track({
                            eventName: BONDS_EVENTS.Clicked_Bonds_Detail_page,
                            eventProperties: {
                              finance_type: deal?.financeType,
                              deal_id: deal?.id,
                            },
                          });
                        }}
                        className={clsx(
                          'relative z-[1] bg-white rounded-lg overflow-hidden border border-zinc-200',
                          {
                            'cursor-pointer': !deal?.isSoldOut,
                          },
                        )}
                      >
                        <div className="px-4 sm:px-6 py-4 flex items-start md:items-center gap-3">
                          <div className="min-w-9 max-w-9 min-h-9 max-h-9 md:max-h-12 md:min-w-12 md:min-h-12 overflow-hidden rounded flex items-center justify-center flex-shrink-0">
                            <Image
                              // @ts-ignore
                              src={deal?.company?.logoUrl}
                              alt={deal?.name}
                              className="object-contain"
                            />
                          </div>
                          <div>
                            <h3 className="text-lg mb-1">{deal?.company?.brandName || ''}</h3>

                            <h4 className="text-slate-500 text-xs">
                              ISIN: {deal?.meta?.isin || ''}
                            </h4>

                            <p className="line-clamp-2 md:truncate text-slate-400 text-sm max-w-xl">
                              {deal?.miniSummary || ''}
                            </p>
                          </div>
                        </div>
                        {deal?.meta?.tags?.length > 0 && typeof deal?.meta?.tags === 'string' && (
                          <div className="flex space-x-2 px-4 sm:px-6 pb-4 ">
                            {deal?.meta?.tags.split(',').map((tag) => (
                              <div className="uppercase text-green-800 bg-green-800/15 px-2.5 py-1.5 rounded text-[8px] font-semibold">
                                {tag}
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="grid grid-cols-2 md:grid-cols-3 max-md:gap-2 px-4 sm:px-6 py-4 border-t border-t-zinc-200">
                          <div>
                            <h3 className="uppercase text-xs text-black/40 font-semibold">YIELD</h3>
                            <h4 className="font-medium text-gray-700">
                              {twoDecimalNumber(deal?.minInvestment?.netIRR) || '-'}%
                            </h4>
                          </div>
                          {/* TODO: Replace with Maturity Date */}
                          <div>
                            <h3 className="uppercase text-xs text-black/40 font-semibold">
                              Tenure
                            </h3>
                            <h4 className="font-medium text-gray-700">
                              {deal?.tenure} {deal?.tenurePeriod || ''}
                            </h4>
                          </div>
                          {/*  */}
                          <div>
                            <h3 className="uppercase text-xs text-black/40 font-semibold">
                              Min Invt.{' '}
                            </h3>
                            <h4 className="font-medium text-green-700">
                              {getRupee.format(deal?.minInvestment?.amount || 0)}
                            </h4>
                          </div>
                        </div>
                      </div>
                      {deal?.isSoldOut && deal?.isSoldOut !== null && (
                        <div className="w-full relative bottom-2 uppercase text-amber-600 text-sm font-semibold text-center pb-2 pt-4 bg-amber-100 rounded-b-lg">
                          Sold out
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>

            <div>
              {profileData?.clientActions?.enableNcdDealsCard && tab === 'BOND' && <NCDDealCard />}
              {profileData?.features?.enableReferral && <ReferAndEarn />}
              {profileData?.clientActions?.enableShowOnlineEvents &&
                (liveCardDetailsLoading ? (
                  <Box
                    className="min-h-40  mt-[22px] border bg-white border-zinc-200 rounded-lg "
                    sx={{
                      border: '1px solid ' + baseColors.zinc[200],
                      borderRadius: '8px',
                      marginBottom: 2,
                      display: 'grid',
                      alignItems: 'center',
                      cursor: 'pointer',
                      overflow: 'hidden',
                      gridTemplateColumns: '150px 1fr',
                    }}
                  >
                    <Skeleton
                      variant="rectangular"
                      width={150}
                      height="100%"
                      sx={{ bgcolor: baseColors.zinc[200] }}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'start',
                        justifyContent: 'space-between',
                        padding: '16px',
                        rowGap: '8px',
                      }}
                    >
                      <Skeleton
                        variant="rectangular"
                        width="60%"
                        height={20}
                        sx={{ borderRadius: '8px' }}
                      />
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'start',
                          flexDirection: 'column',
                          rowGap: '8px',
                        }}
                      >
                        <Skeleton
                          variant="text"
                          width="80%"
                          height={20}
                          sx={{
                            bgcolor: baseColors.zinc[300],
                          }}
                        />
                        <Skeleton
                          variant="text"
                          width="60%"
                          height={15}
                          sx={{
                            bgcolor: baseColors.zinc[300],
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  liveCardDetails && (
                    <Box
                      className="min-h-40  mt-[22px] border bg-white border-zinc-200 rounded-lg "
                      sx={{
                        border: '1px solid ' + baseColors.zinc[200],
                        borderRadius: '8px',
                        marginBottom: 2,
                        display: 'grid',
                        alignItems: 'center',
                        cursor: 'pointer',
                        overflow: 'hidden',
                        gridTemplateColumns: '150px 1fr',
                      }}
                      onClick={() => {
                        window.open(
                          liveCardDetails?.details?.youtubeLiveUrl ||
                            liveCardDetails?.details?.zoomUrl,
                          '_blank',
                        );
                      }}
                    >
                      <Box
                        sx={{
                          height: '100%',
                          background: liveCardDetails?.details?.videoId
                            ? `url(https://img.youtube.com/vi/${liveCardDetails.details.videoId}/0.jpg) left center`
                            : `url(${liveCardDetails?.details?.thumbnailUrl}) left center`,
                          backgroundSize: liveCardDetails?.details?.videoId ? 'auto 140%' : 'cover',
                        }}
                      ></Box>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'start',
                          justifyContent: 'space-between',
                          padding: '16px',
                          rowGap: '8px',
                        }}
                      >
                        <Box
                          sx={{
                            backgroundColor: liveCardDetails?.isLive
                              ? baseColors.red[500]
                              : baseColors.stone[200],
                            color: liveCardDetails?.isLive
                              ? baseColors.white
                              : baseColors.stone[700],
                            padding: '4px 8px',
                            borderRadius: '8px',
                            fontSize: '10px',
                            fontWeight: '500',
                            textTransform: 'uppercase',
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '8px',
                          }}
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 12 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ marginRight: '6px' }}
                          >
                            <path
                              d="M3.17213 2.46879C3.21861 2.51523 3.25549 2.57037 3.28065 2.63107C3.30582 2.69177 3.31877 2.75684 3.31877 2.82254C3.31877 2.88825 3.30582 2.95331 3.28065 3.01401C3.25549 3.07471 3.21861 3.12986 3.17213 3.17629C2.42184 3.9267 2.00035 4.9444 2.00035 6.00554C2.00035 7.06669 2.42184 8.08439 3.17213 8.83479C3.21988 8.88092 3.25797 8.93609 3.28418 8.99709C3.31038 9.05809 3.32417 9.1237 3.32475 9.19009C3.32533 9.25648 3.31268 9.32232 3.28754 9.38377C3.2624 9.44522 3.22527 9.50104 3.17832 9.54799C3.13138 9.59494 3.07555 9.63206 3.0141 9.6572C2.95265 9.68234 2.88681 9.695 2.82042 9.69442C2.75403 9.69384 2.68842 9.68005 2.62742 9.65384C2.56642 9.62764 2.51125 9.58955 2.46513 9.54179C0.511625 7.58879 0.511625 4.42179 2.46513 2.46879C2.55889 2.37506 2.68604 2.3224 2.81863 2.3224C2.95121 2.3224 3.07836 2.37506 3.17213 2.46879ZM9.53813 2.46879C11.4911 4.42229 11.4911 7.58879 9.53813 9.54179C9.44437 9.63561 9.31719 9.68835 9.18455 9.6884C9.05192 9.68844 8.9247 9.6358 8.83087 9.54204C8.73705 9.44829 8.68432 9.32111 8.68427 9.18847C8.68423 9.05584 8.73687 8.92861 8.83063 8.83479C9.58091 8.08439 10.0024 7.06669 10.0024 6.00554C10.0024 4.9444 9.58091 3.9267 8.83063 3.17629C8.7368 3.08247 8.6841 2.95523 8.6841 2.82254C8.6841 2.68986 8.7368 2.56261 8.83063 2.46879C8.92445 2.37497 9.05169 2.32227 9.18437 2.32227C9.31706 2.32227 9.4443 2.37497 9.53813 2.46879ZM4.65563 3.90579C4.74936 3.99956 4.80202 4.12671 4.80202 4.25929C4.80202 4.39188 4.74936 4.51903 4.65563 4.61279C4.4736 4.7948 4.32921 5.01088 4.2307 5.24869C4.13219 5.4865 4.08148 5.74139 4.08148 5.99879C4.08148 6.2562 4.13219 6.51109 4.2307 6.7489C4.32921 6.98671 4.4736 7.20279 4.65563 7.38479C4.70205 7.43125 4.73887 7.48639 4.76398 7.54708C4.78909 7.60776 4.802 7.6728 4.80198 7.73847C4.80195 7.80414 4.78899 7.86917 4.76384 7.92984C4.73869 7.9905 4.70183 8.04562 4.65538 8.09204C4.60892 8.13847 4.55378 8.17528 4.49309 8.20039C4.43241 8.22551 4.36737 8.23842 4.3017 8.2384C4.23602 8.23837 4.171 8.22541 4.11033 8.20026C4.04967 8.17511 3.99455 8.13825 3.94813 8.09179C3.39304 7.53669 3.0812 6.78382 3.0812 5.99879C3.0812 5.21377 3.39304 4.4609 3.94813 3.90579C3.99456 3.85931 4.04971 3.82243 4.1104 3.79726C4.1711 3.7721 4.23617 3.75915 4.30188 3.75915C4.36758 3.75915 4.43265 3.7721 4.49335 3.79726C4.55404 3.82243 4.60919 3.85931 4.65563 3.90579ZM8.13462 3.90579C8.68971 4.4609 9.00155 5.21377 9.00155 5.99879C9.00155 6.78382 8.68971 7.53669 8.13462 8.09179C8.04032 8.18287 7.91402 8.23327 7.78292 8.23213C7.65183 8.23099 7.52642 8.17841 7.43372 8.0857C7.34101 7.993 7.28843 7.86759 7.28729 7.73649C7.28615 7.6054 7.33655 7.47909 7.42763 7.38479C7.60965 7.20279 7.75404 6.98671 7.85255 6.7489C7.95106 6.51109 8.00177 6.2562 8.00177 5.99879C8.00177 5.74139 7.95106 5.4865 7.85255 5.24869C7.75404 5.01088 7.60965 4.7948 7.42763 4.61279C7.33655 4.51849 7.28615 4.39219 7.28729 4.26109C7.28843 4.12999 7.34101 4.00459 7.43372 3.91188C7.52642 3.81918 7.65183 3.7666 7.78292 3.76546C7.91402 3.76432 8.04032 3.81471 8.13462 3.90579ZM6.04163 5.29079C6.24054 5.29079 6.4313 5.36981 6.57196 5.51046C6.71261 5.65112 6.79163 5.84188 6.79163 6.04079C6.79163 6.23971 6.71261 6.43047 6.57196 6.57112C6.4313 6.71178 6.24054 6.79079 6.04163 6.79079C5.84271 6.79079 5.65195 6.71178 5.51129 6.57112C5.37064 6.43047 5.29163 6.23971 5.29163 6.04079C5.29163 5.84188 5.37064 5.65112 5.51129 5.51046C5.65195 5.36981 5.84271 5.29079 6.04163 5.29079Z"
                              fill={
                                liveCardDetails?.isLive ? baseColors.white : baseColors.stone[500]
                              }
                            />
                          </svg>
                          {liveCardDetails?.isLive
                            ? 'Watch now'
                            : 'Going live on ' +
                              moment(liveCardDetails?.details?.startTime).format('DD MMM, h:mm A')}
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'start',
                            flexDirection: 'column',
                            rowGap: '8px',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '14px',
                              fontWeight: '600',
                              color: baseColors.stone[900],
                            }}
                          >
                            {liveCardDetails?.details?.topic}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '12px',
                              fontWeight: '400',
                              color: baseColors.stone[500],
                            }}
                          >
                            {liveCardDetails?.details?.youtubeLiveUrl
                              ? 'YouTube Live'
                              : 'Zoom Webinar'}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )
                ))}
              {profileData?.clientActions?.enableSupportCard && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    // textAlign: "center",
                  }}
                  className={` p-4 pl-0 ${liveCardDetails ? 'pt-0' : 'pt-8'} `}
                >
                  <Typography className="!text-sm font-normal text-slate-800">
                    A Product By{' '}
                    <span style={{ color: '#00000A' }} className="font-medium">
                      Tap Invest
                    </span>
                  </Typography>
                  <Typography className="!text-sm font-normal text-slate-800 mt-2">
                    Facing Issue? Contact us at:{' '}
                    <a
                      href="mailto:support@tapinvest.in"
                      className="cursor-pointer underline underline-offset-2 text-sm font-medium"
                      style={{
                        color: '#00000A',
                      }}
                    >
                      support@tapinvest.in
                    </a>
                  </Typography>
                </Box>
              )}
            </div>

            {/* Refer and Earn Mobile Modal */}
            {profileData?.features?.enableReferral && (
              <Dialog maxWidth="sm" fullWidth onClose={handleClose} open={open}>
                <ReferAndEarn />
              </Dialog>
            )}
          </div>
        </div>
      )}
      {/* Digital Gold Panel */}
      {profileData?.productTypes?.enableGold && (
        <div
          role="tabpanel"
          hidden={tab !== 'GOLD'}
          id={`deals-tabpanel-GOLD`}
          aria-labelledby="digital-gold"
          className={tab !== 'GOLD' ? 'hidden' : 'undefined'}
        >
          <DigitalGold />
        </div>
      )}

      {/* Digital Silver Panel */}
      {profileData?.productTypes?.enableSilver && (
        <div
          role="tabpanel"
          hidden={tab !== 'SILVER'}
          id={`deals-tabpanel-SILVER`}
          aria-labelledby="digital-silver"
          className={tab !== 'SILVER' ? 'hidden' : 'undefined'}
        >
          <DigitalSilver />
        </div>
      )}
      {/* Tap Reserve / P2P */}
      {profileData?.productTypes?.enableTapReserve && !hideP2P && (
        <div
          role="tabpanel"
          hidden={tab !== 'TAP_RESERVE'}
          id={`deals-tabpanel-TAP_RESERVE`}
          aria-labelledby="tapReserve"
          className={tab !== 'TAP_RESERVE' && 'hidden'}
        >
          <TapReserve />
        </div>
      )}
      {profileData?.productTypes?.enablePreIPO && (
        <div
          role="tabpanel"
          hidden={tab !== 'PRE_IPO'}
          id={`deals-tabpanel-PRE_IPO`}
          aria-labelledby="pre-ipo"
          className={tab !== 'PRE_IPO' ? 'hidden' : 'undefined'}
        >
          <PreIpo />
        </div>
      )}
      {profileData?.productTypes?.enableListedBond && (
        <div
          role="tabpanel"
          hidden={tab !== 'LISTED_BONDS'}
          id={`deals-tabpanel-LISTED_BONDS`}
          aria-labelledby="listed-bonds"
          className={tab !== 'LISTED_BONDS' ? 'hidden' : 'undefined'}
        >
          <ListedBondsPage />
        </div>
      )}
      {/* Other Products */}
      {profileData?.productTypes?.enableOtherProducts && (
        <div
          role="tabpanel"
          hidden={tab !== 'OTHER_PRODUCTS'}
          id={`deals-tabpanel-OTHER_PRODUCTS`}
          aria-labelledby="other-products"
          className={tab !== 'OTHER_PRODUCTS' ? 'hidden' : 'undefined'}
        >
          <OtherProducts />
        </div>
      )}
      </ViewOnlyUntilSigned>
      {/* show error page */}
      {/* TODO: To Show in case all products are not available */}
      {/* {tab === 3 && <ErrorPage />} */}
      {/* Cheque Modal */}
      {profileData?.clientActions?.enableSupportCard && (
        <ChequeModal open={openChequeModal} handleClose={handleChequeModalClose} />
      )}
      {/* Whatsapp Modal */}
      {profileData?.features?.enableWhatsappQr && <WhatsappQRComponent />}

      {/* Fullscreen Banner Modal */}
      {fullscreenBanner && (
        <FullscreenBannerModal
          banner={fullscreenBanner}
          onClose={() => setFullscreenBanner(null)}
        />
      )}
    </div>
  );
};

Deals.displayName = 'Deals';

export default Deals;
